import { prepareExamHtml } from '../../utils/examLatexDelimiters';
import { sanitizeHtml } from '../../../../lib/sanitizeHtml';
import './examTables.css';
import './examLists.css';

const BLOCK_TAGS = 'p,div,li,tr,br,blockquote,h1,h2,h3,h4,h5,h6';

export const stripHtmlToText = (html) => {
  if (!html || typeof html !== 'string') return '';
  if (!/<[a-z][\s\S]*>/i.test(html)) return html.trim();
  if (typeof document !== 'undefined') {
    // DOMParser, not a detached div: <img onerror> fires even in a detached div.
    const doc = new DOMParser().parseFromString(html, 'text/html').body;
    // A block boundary is a line break. textContent on its own concatenates
    // across elements with nothing between them, so </p><p> ran the last word
    // of one paragraph into the first word of the next: the LNAT Section B
    // instructions read "Section B: EssayAnswer ONE of the following 21 essay
    // questions.You can use the 'Next'...". parseEssayInstructionParagraphs
    // then split on newlines that were no longer there, so the whole thing
    // came out as one paragraph.
    //
    // Callers that want a single line get one for free: HTML collapses a
    // newline to a space, so nothing that displays this changes.
    doc.querySelectorAll(BLOCK_TAGS).forEach((el) => {
      el.parentNode.insertBefore(doc.ownerDocument.createTextNode('\n'), el);
    });
    return (doc.textContent || '')
      .replace(/[^\S\n]+/g, ' ')   // runs of spaces, tabs and nbsp, but not newlines
      .replace(/ *\n */g, '\n')
      .replace(/\n{2,}/g, '\n')
      .trim();
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

/** Does this option say anything in words?
 *
 * An option can be a picture with no text at all, which is how 224 published
 * questions are answered: "which of the following diagrams" offers five
 * pictures. The text element in an option row takes a flex share, so when it
 * is empty it still claims the whole row and pushes the picture to the far
 * right with a wide blank gap beside it. Ask this first and leave the text
 * element out when there is nothing to put in it.
 */
export const optionHasText = (text) => Boolean(stripHtmlToText(text || '').trim());

export const TABLE_SCROLL_CLASS = 'exam-table-scroll';

const TABLE_CACHE_LIMIT = 500;
const tableCache = new Map();

/** Put each table in a box of its own that scrolls sideways (examTables.css).
 *
 * On a phone a table wider than the question was cut off at the right edge:
 * UCAT Quantitative Reasoning alone had 116 questions whose table lost the
 * columns the question asks about. The table used to be made its own scroll
 * box at narrow widths (display: block), which scrolled but drew the table's
 * border round the full width of the question with the cells short of it, so
 * a three-column table sat in a frame twice its size. A wrapper scrolls
 * instead, and the table keeps its own box, border and width at every size.
 *
 * Only the outermost table is wrapped: a table inside a cell scrolls with the
 * table it sits in. A table already in a wrapper is left alone, so running
 * this twice changes nothing. A <template> parses without running or loading
 * anything, and the sanitiser runs after this, so it still has the last word. */
export const wrapTablesForScroll = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  if (!/<table[\s>/]/i.test(html) || typeof document === 'undefined') return html;
  const hit = tableCache.get(html);
  if (hit !== undefined) return hit;
  const template = document.createElement('template');
  template.innerHTML = html;
  let wrapped = 0;
  template.content.querySelectorAll('table').forEach((table) => {
    const parent = table.parentElement;
    if (parent && (parent.closest('table') || parent.classList.contains(TABLE_SCROLL_CLASS))) return;
    const box = document.createElement('div');
    box.className = TABLE_SCROLL_CLASS;
    table.parentNode.insertBefore(box, table);
    box.appendChild(table);
    wrapped += 1;
  });
  const out = wrapped ? template.innerHTML : html;
  if (tableCache.size >= TABLE_CACHE_LIMIT) tableCache.delete(tableCache.keys().next().value);
  tableCache.set(html, out);
  return out;
};

export const ORDERED_LIST_CLASS = 'exam-ol';

// The marker each value of an <ol type> asks for. The value is case-sensitive
// (a and A, i and I are different lists), which a CSS attribute selector on
// type cannot tell apart in an HTML document without the s flag, so the list
// is given a class that says which it is.
const OL_TYPE_MARKERS = {
  1: 'decimal',
  a: 'lower-alpha',
  A: 'upper-alpha',
  i: 'lower-roman',
  I: 'upper-roman',
};

const LIST_CACHE_LIMIT = 500;
const listCache = new Map();

/** Number each ordered list in a paper's HTML (examLists.css).
 *
 * The site's reset (index.css) takes the markers off every list, which is
 * right for menus and navigation but drew a question's <ol> with no numbers:
 * the statements an option calls "1 and 3" in a Decision Making explanation
 * or an ESAT stem, and the proof lines a TMUA option calls "Line 3", stood
 * unnumbered. Each <ol> is given a class that numbers it again, with a second
 * class for its type attribute when it has one (type="I" numbers I, II, III);
 * start, reversed and an <li value> keep working, since the browser counts
 * them whatever the marker style. A <ul> is left as it is.
 *
 * A list that already has the class is left alone, so running this twice
 * changes nothing. A <template> parses without running or loading anything,
 * and the sanitiser runs after this, so it still has the last word. */
export const numberOrderedLists = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  if (!/<ol[\s>/]/i.test(html) || typeof document === 'undefined') return html;
  const hit = listCache.get(html);
  if (hit !== undefined) return hit;
  const template = document.createElement('template');
  template.innerHTML = html;
  let marked = 0;
  template.content.querySelectorAll('ol').forEach((ol) => {
    if (ol.classList.contains(ORDERED_LIST_CLASS)) return;
    ol.classList.add(ORDERED_LIST_CLASS);
    const marker = OL_TYPE_MARKERS[(ol.getAttribute('type') || '').trim()];
    if (marker) ol.classList.add(`${ORDERED_LIST_CLASS}--${marker}`);
    marked += 1;
  });
  const out = marked ? template.innerHTML : html;
  if (listCache.size >= LIST_CACHE_LIMIT) listCache.delete(listCache.keys().next().value);
  listCache.set(html, out);
  return out;
};

/** Exam HTML as the paper player renders it: delimiters repaired by
 * prepareExamHtml, tables given a box to scroll in, ordered lists numbered,
 * then through the one sanitiser, before MathJax sees it. */
export const prepareSittingHtml = (text) => sanitizeHtml(
  numberOrderedLists(wrapTablesForScroll(prepareExamHtml(text))),
);

// One formula and nothing else, optionally in one paragraph: $...$ or \(...\).
// A $...$ body holds no unescaped $, and a \(...\) body no \) before its end,
// so "$a$ or $b$" and a sentence with maths in it do not match.
const LONE_DOLLAR_FORMULA = /^(\s*(?:<p>\s*)?\$)((?:[^$\\]|\\[\s\S])+)(\$\s*(?:<\/p>\s*)?)$/;
const LONE_PAREN_FORMULA = /^(\s*(?:<p>\s*)?\\\()((?:(?!\\\))[\s\S])+)(\\\)\s*(?:<\/p>\s*)?)$/;
const STYLE_SET = /^\s*\\(?:displaystyle|textstyle|scriptstyle|scriptscriptstyle)\b/;

/** An option that is a formula and nothing else, set as print sets it.
 *
 * Inline maths is typeset in text style, which draws a fraction's numerator
 * and denominator at script size (70.7%) round a hairline bar. In running text
 * that is right; as the whole of an answer option it is not: -6/5 came out
 * with 10px digits beside a 13px radio button, and the layout review of the
 * live ESAT Mathematics 2 papers (18 October 2026) read the options of
 * ESAT-3002-sE q9, ESAT-3009-sE q27 and ESAT-3015-sE q15 as "small stray
 * fraction fragments" beside the radio buttons. A paper sets an answer that is
 * a value in display style, at the size of the text round it, so an option
 * that is one inline formula and nothing else is given \displaystyle. An
 * option with words in it, or more than one formula, keeps text style, as
 * maths in a sentence does; display maths ($$...$$) is display style already,
 * and a formula that sets its own style is left as it is. */
export const displayStyleLoneFormula = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  const m = html.match(LONE_DOLLAR_FORMULA) || html.match(LONE_PAREN_FORMULA);
  if (!m || STYLE_SET.test(m[2])) return html;
  return `${m[1]}\\displaystyle ${m[2]}${m[3]}`;
};

/** An answer option's HTML as the paper player renders it: prepareSittingHtml,
 * and an option that is one formula set in display style. */
export const prepareOptionHtml = (text) => displayStyleLoneFormula(prepareSittingHtml(text));

// Notation that stacks one thing above another: a fraction, a binomial, an
// array or matrix. \over and friends are TeX's infix forms of the same.
const STACKED_NOTATION = /\\(?:[dtc]?frac|[dt]?binom|over|atop|choose)(?![A-Za-z])|\\begin\s*\{/;
const LONE_DISPLAY_FORMULA = /^\s*(?:<p>\s*)?\$\$((?:[^$\\]|\\[\s\S])+)\$\$\s*(?:<\/p>\s*)?$/;

const STACKED_CACHE_LIMIT = 500;
const stackedCache = new Map();

/** Is this option a formula and nothing else, with a fraction (or anything
 * else stacked) in it?
 *
 * Such an option is set in display style (displayStyleLoneFormula, or $$...$$
 * already), as tall as two lines of text. ESAT-3006-sE q12's eight options,
 * 1/9, 1/6, 1/4, 1/3, 2/3, 3/4, 1, 3, were set so, and sat 10 px apart, the
 * gap a line of text gets: a column of digit, bar, digit, digit, bar, digit,
 * with nothing but that gap to show where one option ended and the next
 * began, which the layout review (18 October 2026) read as cramped fractions.
 * The option list asks this to give such options more room (optionListGap,
 * optionRow.js). An option with words in it keeps text style, a line high,
 * and answers no. */
export const optionIsStackedFormula = (text) => {
  if (!text || typeof text !== 'string') return false;
  const hit = stackedCache.get(text);
  if (hit !== undefined) return hit;
  const html = prepareSittingHtml(text);
  const m = html.match(LONE_DOLLAR_FORMULA) || html.match(LONE_PAREN_FORMULA);
  const d = m ? null : html.match(LONE_DISPLAY_FORMULA);
  const body = m ? m[2] : d ? d[1] : '';
  const out = Boolean(body) && STACKED_NOTATION.test(body)
    && !(m && /^\s*\\(?:textstyle|scriptstyle|scriptscriptstyle)\b/.test(body));
  if (stackedCache.size >= STACKED_CACHE_LIMIT) stackedCache.delete(stackedCache.keys().next().value);
  stackedCache.set(text, out);
  return out;
};
